/**
 * copyToClipboard — robust clipboard helper that works on iOS Safari, Android
 * Chrome, desktop browsers, and even http (non-https) origins.
 *
 * Strategy (in order):
 *  1. navigator.clipboard.writeText (modern, requires https + user gesture)
 *  2. document.execCommand("copy") fallback via temporary textarea (legacy,
 *     works on iOS even if clipboard API is missing)
 *  3. navigator.share (last resort: lets user pick destination)
 *
 * @param {string} text - The text to copy.
 * @param {object} [opts] - Options.
 * @param {string} [opts.shareTitle] - Title for navigator.share fallback.
 * @returns {Promise<"clipboard" | "share" | "manual">} - How it was handled.
 *   "manual" means user has to copy manually; caller should show the text.
 */
export async function copyToClipboard(text, opts = {}) {
  if (!text) return "manual";

  // 1) Modern Clipboard API (iOS 13.4+, all modern desktops/Android)
  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return "clipboard";
    } catch {
      // user denied permission or browser blocked → fall through
    }
  }

  // 2) Legacy execCommand fallback — works on older iOS Safari
  if (typeof document !== "undefined" && document.execCommand) {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      // Place off-screen but selectable. iOS Safari requires not display:none.
      textarea.style.position = "fixed";
      textarea.style.top = "-100vh";
      textarea.style.left = "0";
      textarea.style.opacity = "0";
      textarea.setAttribute("readonly", "");
      document.body.appendChild(textarea);
      // iOS needs explicit range selection
      if (/iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream) {
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        textarea.setSelectionRange(0, text.length);
      } else {
        textarea.select();
      }
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (ok) return "clipboard";
    } catch {
      // fall through to share or manual
    }
  }

  // 3) Last resort: trigger Web Share API so user picks WhatsApp/Notes/Copy
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({
        title: opts.shareTitle || "Link",
        text,
        url: /^https?:\/\//.test(text) ? text : undefined,
      });
      return "share";
    } catch {
      // user dismissed → fall through
    }
  }

  return "manual";
}
